import { Modal } from "components";
import { useEffect, useCallback, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import SelectWifiNetworkModal from "./SelectWifiNetworkModal";
import { getNetworkSettings, updateNetworkSettings } from "actions/network";
import { useDispatch, useSelector } from "react-redux";
import { CheckCircle, WarningRounded } from "@mui/icons-material";
import {
  ConfigureNetwork,
  EnterWifiNetwork,
  LinearLoader,
} from "empower-energy-owner-components";

// Memoized styles for better performance on rerender
const styles = {
  "& .button-footer": {
    mt: 1,
    pt: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    borderTop: "1px solid rgba(0, 64, 87, 0.25)",
  },
  "& .updating-message": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    wordBreak: "break-word",
    "& .check-icon": {
      marginRight: "12px",
      fontSize: "40px",
    },
    "& .warning-icon": {
      color: "#F9B426",
      marginRight: "12px",
      fontSize: "40px",
    },
  },
};

const STATUS = {
  configure: "configure",
  selectWifi: "selectWifi",
  enterWifi: "enterWifi",
};

const MESSAGE_STATUS = {
  success: "success",
  fail: "fail",
  customMessage: "customMessage",
  systemMessage: "systemMessage",
};

const NetworkModal = ({ open, onClose, selectedUnit }) => {
  const dispatch = useDispatch();

  const [status, setStatus] = useState(STATUS.configure);
  const [selectedWifi, setSelectedWifi] = useState(null);
  const [settingsUpdated, setSettingsUpdated] = useState(false);
  const [messageStatus, setMessageStatus] = useState(
    MESSAGE_STATUS.customMessage,
  );

  const networkSettings = useSelector(
    (state) => state.network?.networkSettings,
  );
  const previousNetworkSettings = useSelector(
    (state) => state.network?.previousNetworkSettings,
  );
  const networkSettingsLoading = useSelector(
    (state) => state.network?.networkSettingsLoading,
  );

  // Fetch network settings when modal opens or selectedUnit changes
  useEffect(() => {
    if (selectedUnit?.id) {
      dispatch(getNetworkSettings(selectedUnit.id));
    }
  }, [dispatch, selectedUnit]);

  // Poll for status updates when settings are updating
  useEffect(() => {
    let interval;
    if (settingsUpdated) {
      dispatch(getNetworkSettings(selectedUnit.id));
      interval = setInterval(() => {
        dispatch(getNetworkSettings(selectedUnit.id));
      }, 5000);
    }
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [dispatch, selectedUnit, settingsUpdated]);

  // Smart message status calculation
  useEffect(() => {
    const currMsg = networkSettings?.status?.last_message;
    const prevMsg = previousNetworkSettings?.status?.last_message;
    const updating = networkSettings?.status?.updating;
    const success = networkSettings?.status?.success;

    if (!prevMsg || currMsg > prevMsg) {
      if (!updating) {
        setMessageStatus(
          success ? MESSAGE_STATUS.success : MESSAGE_STATUS.fail,
        );
      } else {
        setMessageStatus(MESSAGE_STATUS.systemMessage);
      }
    } else {
      setMessageStatus(MESSAGE_STATUS.customMessage);
    }
  }, [networkSettings, previousNetworkSettings]);

  // Get modal title dynamically
  const getTitle = () => {
    switch (status) {
      case STATUS.selectWifi:
        return "Select WiFi Network";
      case STATUS.enterWifi:
        return selectedWifi?.ssid === "enterDetails"
          ? "Enter WiFi network"
          : `Enter the password for “${selectedWifi?.ssid || selectedWifi?.bssid}”`;
      case STATUS.configure:
      default:
        return "Configure Network";
    }
  };

  // Memoize handlers for clarity and efficiency
  const handleSelectWifi = useCallback((wifi) => {
    setSelectedWifi(wifi);
    setStatus(STATUS.enterWifi);
  }, []);

  const handleConnectWifi = useCallback(
    (data) => {
      setSettingsUpdated(false);
      dispatch(
        updateNetworkSettings(selectedUnit.id, data, () =>
          setSettingsUpdated(true),
        ),
      );
    },
    [dispatch, selectedUnit],
  );

  const handleConfigureNetwork = useCallback(
    (data) => {
      setSettingsUpdated(false);
      dispatch(
        updateNetworkSettings(selectedUnit.id, data, () =>
          setSettingsUpdated(true),
        ),
      );
    },
    [dispatch, selectedUnit],
  );

  return (
    <Modal
      width="520px"
      top="10%"
      title={getTitle()}
      open={open}
      onClose={onClose}
      showHeaderBorder
    >
      <Box mt={2}>
        {!settingsUpdated ? (
          <Box>
            {status === STATUS.configure && (
              <ConfigureNetwork
                loading={networkSettingsLoading}
                networkSettings={networkSettings}
                onCancel={onClose}
                onClickSave={handleConfigureNetwork}
                onClickSelectNetwork={() => setStatus(STATUS.selectWifi)}
              />
            )}
            {status === STATUS.selectWifi && (
              <SelectWifiNetworkModal
                selectedUnit={selectedUnit}
                onCancel={() => setStatus(STATUS.configure)}
                onClickSelectWifiNetwork={handleSelectWifi}
              />
            )}
            {status === STATUS.enterWifi && (
              <EnterWifiNetwork
                selectedWifi={selectedWifi}
                onCancel={() => setStatus(STATUS.selectWifi)}
                onClickSave={handleConnectWifi}
              />
            )}
          </Box>
        ) : (
          <Box sx={styles}>
            <Box py={1}>
              {messageStatus === MESSAGE_STATUS.success && (
                <Box className="updating-message">
                  <CheckCircle className="check-icon" color="success" />
                  <Typography variant="h4">
                    {networkSettings?.status?.message}
                  </Typography>
                </Box>
              )}
              {messageStatus === MESSAGE_STATUS.fail && (
                <Box className="updating-message">
                  <WarningRounded className="warning-icon" />
                  <Typography variant="h4">
                    {networkSettings?.status?.message}
                  </Typography>
                </Box>
              )}
              {messageStatus === MESSAGE_STATUS.customMessage && (
                <Box width="100%">
                  <LinearLoader message="Updating network settings" />
                </Box>
              )}
              {messageStatus === MESSAGE_STATUS.systemMessage && (
                <Box width="100%">
                  <LinearLoader
                    message={
                      networkSettings?.status?.message ||
                      "Updating network settings"
                    }
                  />
                </Box>
              )}
            </Box>
            <Box className="button-footer">
              <Button onClick={onClose}>Dismiss</Button>
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default NetworkModal;
